import { Leaf, Droplets, Wind, Waves, Trash2, FileSearch, UserCheck, Thermometer, CloudSun, Gauge, ClipboardCheck } from "lucide-react";
import { SiteHeader } from "@/components/marketing/SiteHeader";
import { SiteFooter } from "@/components/marketing/SiteFooter";
import { PricingBlock } from "@/components/marketing/PricingBlock";
import { IncludedBadge } from "@/components/marketing/IncludedBadge";
import { CtaButtons } from "@/components/marketing/CtaButtons";
import { WorksWith, Partners } from "@/components/marketing/WorksWithPartners";

const REPORTING = [
  { icon: Droplets, t: "Spills", d: "Record what was released, where, and the containment steps taken." },
  { icon: Wind, t: "Air", d: "Log emissions, dust and odor issues alongside the conditions at the time." },
  { icon: Waves, t: "Water", d: "Capture stormwater and discharge issues with location and follow-up." },
  { icon: Trash2, t: "Waste", d: "Track waste handling and storage issues through to close-out." },
];
const CONDITIONS = [
  { icon: Wind, t: "Wind", d: "Wind speed and direction shape how dust, vapors and spills travel." },
  { icon: Thermometer, t: "Heat", d: "Heat changes the risk for outdoor work and for stored materials." },
  { icon: CloudSun, t: "Weather", d: "Rain, storms and temperature swings change what a safe plan looks like." },
];

const Kicker = ({ children, light }) => (
  <span className={`text-xs uppercase tracking-[0.2em] font-semibold ${light ? "text-[#CFE0D2]" : "text-[#275C3F]"}`}>{children}</span>
);
const H2 = ({ children }) => <h2 className="font-display text-3xl lg:text-4xl font-bold tracking-tight mt-3">{children}</h2>;
const Card = ({ icon: Icon, t, d }) => (
  <div className="bg-white border border-[#DCE3DD] rounded-sm p-6 hover:border-[#275C3F]/40 transition-colors duration-200">
    <div className="h-11 w-11 grid place-items-center bg-[#EAF0EA] rounded-sm"><Icon className="h-5 w-5 text-[#275C3F]" /></div>
    <h3 className="font-display text-xl font-semibold mt-4">{t}</h3>
    <p className="text-[#4B5950] mt-2 text-sm leading-relaxed">{d}</p>
  </div>
);

const Hero = () => (
  <section className="border-b border-[#DCE3DD]">
    <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-12 items-center py-16 md:py-24">
      <div>
        <IncludedBadge testid="hero-included-badge" />
        <h1 data-testid="hero-title" className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05] mt-5">
          Environmental conditions change the risk. <span className="text-[#275C3F]">EnviSafe keeps you ahead of both.</span>
        </h1>
        <p data-testid="hero-sub" className="text-lg text-[#4B5950] mt-5 max-w-xl leading-relaxed">
          Log spills, air, water and waste issues with suggested EPA and OSHA references, and bring site conditions like wind and heat into every hazard decision.
        </p>
        <div className="mt-8"><CtaButtons prefix="hero" /></div>
      </div>
      <video data-testid="hero-video" autoPlay muted loop playsInline
        src="https://customer-assets.emergentagent.com/wingman/83287065-a81d-4252-b3dd-18a513204039/attachments/759f883134874f5ab3e44fd5271c4e82_envisafe_hero_video.mp4"
        poster="https://images.unsplash.com/photo-1716643863806-989dd76ae093?crop=entropy&cs=srgb&fm=jpg&q=85"
        className="rounded-sm border border-[#DCE3DD] w-full h-[420px] object-cover bg-[#121A14]" />
    </div>
  </section>
);

const Reporting = () => (
  <section id="reporting" className="max-w-7xl mx-auto px-6 py-20" data-testid="section-reporting">
    <Kicker>Environmental reporting</Kicker>
    <H2>Spills, air, water and waste in one record.</H2>
    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-10">{REPORTING.map((r) => <Card key={r.t} {...r} />)}</div>
  </section>
);

const References = () => (
  <section id="references" className="bg-white border-y border-[#DCE3DD]" data-testid="section-references">
    <div className="max-w-7xl mx-auto px-6 py-20 grid lg:grid-cols-2 gap-12 items-start">
      <div>
        <Kicker>Suggested EPA/OSHA references</Kicker>
        <H2>References are suggested, reviewed by a person.</H2>
      </div>
      <div className="space-y-5 text-[#4B5950] leading-relaxed">
        <p className="flex gap-3"><FileSearch className="h-5 w-5 text-[#275C3F] shrink-0 mt-0.5" /> When an issue is logged, EnviSafe suggests EPA and OSHA references that may apply.</p>
        <p className="flex gap-3"><UserCheck className="h-5 w-5 text-[#275C3F] shrink-0 mt-0.5" /> Every AI output is a suggestion. A person reviews it, edits it, and decides what goes on the record.</p>
      </div>
    </div>
  </section>
);

const Conditions = () => (
  <section id="conditions" className="max-w-7xl mx-auto px-6 py-20" data-testid="section-conditions">
    <Kicker>Site conditions that change the risk</Kicker>
    <H2>Wind, heat and weather, inside every hazard decision.</H2>
    <div className="grid md:grid-cols-3 gap-4 mt-10">{CONDITIONS.map((c) => <Card key={c.t} {...c} />)}</div>
  </section>
);

const Readiness = () => (
  <section id="readiness" className="bg-[#275C3F] text-white" data-testid="section-readiness">
    <div className="max-w-7xl mx-auto px-6 py-20 grid lg:grid-cols-2 gap-12 items-center">
      <div>
        <Kicker light>WSafe Readiness</Kicker>
        <H2>The site-conditions source for WSafe Readiness.</H2>
        <p className="text-white/80 mt-4 text-lg leading-relaxed">
          EnviSafe supplies site conditions to WSafe Readiness &mdash; WSafe AI's read on whether current site conditions make it safe to work right now. Readiness is conditions-only: it looks at the site, not the person, and makes no individual fatigue inference.
        </p>
      </div>
      <div className="flex items-center gap-4 border border-white/15 rounded-sm p-6">
        <Gauge className="h-10 w-10 text-[#CFE0D2] shrink-0" />
        <p className="text-white/80 text-sm leading-relaxed">Conditions in, readiness context out. No individual monitoring.</p>
      </div>
    </div>
  </section>
);

const Records = () => (
  <section id="records" className="max-w-7xl mx-auto px-6 py-20" data-testid="section-records">
    <div className="grid lg:grid-cols-2 gap-12 items-start">
      <div>
        <Kicker>Environmental records in the Compliance service</Kicker>
        <H2>Environmental records, alongside OSHA records.</H2>
      </div>
      <p className="flex gap-3 text-[#4B5950] leading-relaxed">
        <ClipboardCheck className="h-5 w-5 text-[#275C3F] shrink-0 mt-0.5" />
        The Compliance Evaluation & Documentation Service tiers cover environmental records as well as OSHA records, so EnviSafe reports feed the same documentation workflow.
      </p>
    </div>
  </section>
);

const CtaBand = () => (
  <section className="bg-[#121A14] text-white border-b border-white/10" data-testid="section-cta">
    <div className="max-w-7xl mx-auto px-6 py-16 flex flex-col lg:flex-row lg:items-center justify-between gap-8">
      <div className="flex items-center gap-3">
        <Leaf className="h-6 w-6 text-[#6D9C55]" />
        <p className="font-display text-2xl font-bold">Prevent the incident. Don't reconstruct it.</p>
      </div>
      <CtaButtons prefix="band" dark />
    </div>
  </section>
);

export default function Landing() {
  return (
    <div className="min-h-screen bg-[#F8FAF8] text-[#121A14]">
      <SiteHeader />
      <Hero />
      <Reporting />
      <References />
      <Conditions />
      <Readiness />
      <Records />
      <WorksWith />
      <Partners />
      <PricingBlock />
      <CtaBand />
      <SiteFooter />
    </div>
  );
}
